import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import JourneyCard from "@/components/feature/JourneyCard";
import JourneyTimeline from "@/pages/journeys/components/JourneyTimeline";
import { externalLinks, photos } from "@/mocks/site";
import { journeys } from "@/mocks/events";

export default function CulinaryJourneysPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="World 03 · The Travel"
        title={
          <>
            THE CULINARY JOURNEYS OF
            <br />
            CHEF EDDIE &amp; KIKI G
          </>
        }
        subtitle="A living travel journal — follow Chef Eddie & Kiki G around the world through food, people and places, from October 2026 into 2027."
        image={photos.journeysHero}
        imageClassName="object-center"
        meta={
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <a href="#timeline" className="btn-primary">
              Upcoming Journeys
              <i className="ri-arrow-down-line text-base" />
            </a>
            <Link
              to="/invite-eddie"
              className="btn-ghost text-background-50/85 hover:bg-background-50 hover:text-foreground-950"
            >
              Invite Eddie
            </Link>
          </div>
        }
      />

      <JourneyTimeline />

      {/* Eddie & Kiki — the personal side of the journey */}
      <section className="relative overflow-hidden bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 pt-20 md:pt-28">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal variant="mask">
                <div className="aspect-[4/5] w-full overflow-hidden bg-foreground-900 image-zoom">
                  <img
                    src={photos.coupleTravel}
                    alt="Chef Eddie & Kiki G travelling together"
                    title="Eddie & Kiki — the journey is better together"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal>
                <p className="eyebrow">Eddie &amp; Kiki</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                  The journey is better{" "}
                  <span className="italic text-primary-600">together.</span>
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-foreground-700">
                  These journeys are not just destinations on a map. They are the
                  shared experience of food, culture and the people met along the
                  way — the reason a place is worth returning to at all.
                </p>
              </Reveal>
              <Reveal delay={260}>
                <a
                  href={externalLinks.chefEddieG}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline mt-9 inline-flex w-fit items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700"
                >
                  Meet Eddie &amp; Kiki
                  <i className="ri-arrow-right-up-line text-base" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Oversized, full-bleed editorial moment */}
        <Reveal variant="mask" delay={160}>
          <figure className="mt-16 w-full overflow-hidden image-zoom md:mt-24">
            <div className="aspect-[4/5] w-full overflow-hidden bg-foreground-900 sm:aspect-[16/10] md:aspect-[21/9]">
              <img
                src={photos.coupleFormal}
                alt="Chef Eddie G and Kiki G walking hand in hand"
                title="Eddie & Kiki — Chef Eddie G"
                className="h-full w-full object-cover object-center"
              />
            </div>
          </figure>
        </Reveal>
      </section>

      <section className="relative flex min-h-[55vh] items-center justify-center overflow-hidden md:min-h-[65vh]">
        <div className="absolute inset-0">
          <img
            src={photos.road}
            alt="Chef Eddie G on the open road"
            title="From the road — Chef Eddie G"
            className="h-full w-full object-cover object-center animate-kenburns"
          />
        </div>
        <div className="absolute inset-0 bg-foreground-950/55" />
        <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 py-16 text-center">
          <Reveal>
            <p className="eyebrow text-primary-300">Between the Journeys</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mx-auto mt-5 max-w-3xl font-heading text-3xl leading-[1.05] text-background-50 sm:text-4xl md:text-5xl">
              FROM THE <span className="italic text-primary-300">ROAD</span>
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mx-auto mt-6 max-w-2xl font-body text-base leading-relaxed text-background-50/75 md:text-lg">
              Every destination begins long before the arrival — with a road,
              a market and a reason to keep going.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow">Journey Archive</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                  The journeys{" "}
                  <span className="italic text-primary-600">already told.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
              <Reveal delay={200}>
                <p className="font-body text-base leading-relaxed text-foreground-700">
                  Stories from journeys already taken. The archive grows as new
                  destinations are added to the calendar.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {journeys.map((journey, i) => (
              <Reveal key={journey.slug} delay={i * 90}>
                <JourneyCard journey={journey} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="The Invitation"
        title={
          <>
            Want Eddie to
            <br />
            <span className="italic text-primary-400">come to you?</span>
          </>
        }
        text="Every journey starts with someone opening a door. Invite Eddie to your city, your event or your table."
        primary={{ label: "Invite Eddie", to: "/invite-eddie" }}
        secondary={{ label: "Submit a Locavore", to: "/submit-a-locavore" }}
      />
    </SiteLayout>
  );
}